import React, { useState, useEffect, useRef } from 'react';
import './Stylekit.css';
import logo from '../../../assets/logo.svg';
import Button from '../../Button/Button';

function Stylekit({ data }) {
  const [copiedColor, setCopiedColor] = useState(null);
  const [visibleColors, setVisibleColors] = useState(new Set());
  const colorRefs = useRef([]);

  // Animate color swatches when they come into view
  useEffect(() => {
    if (!data) return;

    const observers = [];

    colorRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleColors((prev) => new Set([...prev, index]));
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [data]);

  if (!data) return null;

  // Color definitions from the style kit
  const colors = [
    { name: 'Space Opera', hex: '#4F16D3' },
    { name: 'Royal Purple', hex: '#7851A9' },
    { name: 'Pale Lavender', hex: '#D6CDFF' },
    { name: 'Metallic Gray', hex: '#2A2929' },
  ];

  const handleColorClick = (color) => {
    navigator.clipboard.writeText(color.hex).then(() => {
      setCopiedColor(color.hex);
      setTimeout(() => setCopiedColor(null), 2000);
    }).catch(err => {
      console.error('Failed to copy color:', err);
    });
  };

  const handleLogoDownload = async () => {
    try {
      // Get the logo URL (handles webpack-processed imports)
      const logoUrl = typeof logo === 'string' ? logo : logo.default || logo;
      
      // Fetch the logo file
      const response = await fetch(logoUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch logo');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'kcgu-logo.svg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download logo:', error);
      // Fallback: try to download using the img element's src
      try {
        const img = document.querySelector('.stylekit-logo');
        if (img && img.src) {
          const link = document.createElement('a');
          link.href = img.src;
          link.download = 'kcgu-logo.svg';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
      }
    }
  };

  // Font definitions from the style kit
  const fonts = [
    {
      name: 'Header 1',
      family: 'Inter',
      weight: 'Extra Bold',
      weightValue: 800,
      size: '64px',
      sample: 'The quick brown fox jumped over the lazy dog.',
      googleFontsUrl: 'https://fonts.google.com/specimen/Inter',
    },
    {
      name: 'Header 2',
      family: 'Instrument Sans',
      weight: 'Semi Bold',
      weightValue: 600,
      size: '40px',
      sample: 'The quick brown fox jumped over the lazy dog.',
      googleFontsUrl: 'https://fonts.google.com/specimen/Instrument+Sans',
    },
    {
      name: 'Subtitle',
      family: 'Instrument Sans',
      weight: 'Headline Small',
      weightValue: 400,
      size: '24px',
      sample: 'The quick brown fox jumped over the lazy dog.',
      googleFontsUrl: 'https://fonts.google.com/specimen/Instrument+Sans',
    },
    {
      name: 'Caption',
      family: 'Instrument Sans',
      weight: 'Headline Small',
      weightValue: 400,
      size: '16px',
      sample: 'The quick brown fox jumped over the lazy dog.',
      googleFontsUrl: 'https://fonts.google.com/specimen/Instrument+Sans',
    },
  ];

  return (
    <section className="stylekit-section">
      <div className="container">
        <div className="stylekit-content">
          <h1 className="stylekit-title">Style Kit</h1>
          <p className="stylekit-subtitle">This is our visual identity guide. We use it to ensure consistency across all our materials.</p>

          {/* Colors Section */}
          <div className="stylekit-colors-section">
            <h2 className="stylekit-section-heading">Colors</h2>
            <div className="stylekit-colors-grid">
              {colors.map((color, index) => {
                const isVisible = visibleColors.has(index);
                return (
                  <div 
                    key={index} 
                    className="stylekit-color-item"
                    ref={(el) => (colorRefs.current[index] = el)}
                  >
                    <div
                      className={`stylekit-color-swatch ${isVisible ? 'stylekit-color-swatch-visible' : ''}`}
                      style={{ 
                        backgroundColor: color.hex,
                        animationDelay: `${index * 0.1}s`
                      }}
                      title={`${color.name}: ${color.hex} - Click to copy`}
                      onClick={() => handleColorClick(color)}
                    >
                    {copiedColor === color.hex && (
                      <span className="stylekit-copy-feedback">Copied!</span>
                    )}
                  </div>
                  <div className={`stylekit-color-info ${isVisible ? 'stylekit-color-info-visible' : ''}`}>
                    <span className="stylekit-color-name">{color.name}</span>
                    <span 
                      className="stylekit-color-hex"
                      onClick={() => handleColorClick(color)}
                      title="Click to copy"
                    >
                      {color.hex}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Fonts Section */}
          <div className="stylekit-fonts-section">
            <h2 className="stylekit-section-heading">Fonts</h2>
            <div className="stylekit-fonts-list">
              {fonts.map((font, index) => (
                <div key={index} className="stylekit-font-item">
                  <div className="stylekit-font-label">
                    <span className="stylekit-font-name">{font.name}</span>
                    <span className="stylekit-font-spec">
                      {font.family} - {font.weight} | {font.size}
                    </span>
                    <a 
                      href={font.googleFontsUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="stylekit-font-link"
                    >
                      View on Google Fonts →
                    </a>
                  </div>
                  <div
                    className="stylekit-font-sample"
                    style={{
                      fontFamily: font.family,
                      fontWeight: font.weightValue,
                      fontSize: font.size,
                    }}
                  >
                    {font.sample}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Logo Section */}
          <div className="stylekit-logo-section">
            <h2 className="stylekit-section-heading">Logo</h2>
            <div className="stylekit-logo-display">
              <img src={logo} alt="KCGU Logo" className="stylekit-logo" />
              <Button 
                onClick={handleLogoDownload}
                aria-label="Download KCGU logo"
                className='btn-small'
              >
                Download Logo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Stylekit;
